import { computed, Injectable, signal } from '@angular/core';
import { safeJsonParse } from '@core/utils/safe-json-parse';

@Injectable({
  providedIn: 'root',
})
export class MySpaceBadgesService {
  static readonly MY_SPACE_BADGES_LOCAL_STORAGE_KEY = 'FLUX_CONTROL.MY_SPACE_BADGES';

  public readonly _badges = signal<
    Record<
      'my-cards' | 'my-tasks' | 'my-reviews' | 'my-solicitations',
      {
        lastUpdate: number;
        lastSeen: number;
      }
    >
  >({
    'my-cards': { lastUpdate: 0, lastSeen: 0 },
    'my-tasks': { lastUpdate: 0, lastSeen: 0 },
    'my-reviews': { lastUpdate: 0, lastSeen: 0 },
    'my-solicitations': { lastUpdate: 0, lastSeen: 0 },
  });

  public readonly badges = this._badges.asReadonly();

  public unreadBadges = computed(() => {
    return Object.entries(this._badges()).reduce(
      (acc, [key, value]) => {
        if (value.lastUpdate > value.lastSeen) {
          acc[key as 'my-cards' | 'my-tasks' | 'my-reviews' | 'my-solicitations'] = true;
        }
        return acc;
      },
      {} as Record<'my-cards' | 'my-tasks' | 'my-reviews' | 'my-solicitations', boolean>,
    );
  });

  public hasUnreadBadges = computed(() => {
    return Object.values(this.unreadBadges()).some((value) => value);
  });

  public constructor() {
    const storedBadges = localStorage.getItem(
      MySpaceBadgesService.MY_SPACE_BADGES_LOCAL_STORAGE_KEY,
    );
    if (storedBadges) {
      try {
        const parsedBadges =
          safeJsonParse<Record<string, { lastUpdate: number; lastSeen: number }>>(storedBadges);

        if (!parsedBadges) {
          console.warn('Stored badges are not in the expected format, resetting to default.');
          this._badges.set({
            'my-cards': { lastUpdate: 0, lastSeen: 0 },
            'my-tasks': { lastUpdate: 0, lastSeen: 0 },
            'my-reviews': { lastUpdate: 0, lastSeen: 0 },
            'my-solicitations': { lastUpdate: 0, lastSeen: 0 },
          });
          localStorage.removeItem(MySpaceBadgesService.MY_SPACE_BADGES_LOCAL_STORAGE_KEY);
          return;
        }

        this._badges.set(parsedBadges);
      } catch (error) {
        console.error('Failed to parse stored badges:', error);
      }
    }
  }

  public markTabAsSeen(tab: 'my-cards' | 'my-tasks' | 'my-reviews' | 'my-solicitations'): void {
    const currentTime = Date.now();
    this._badges.update((badges) => {
      badges[tab].lastSeen = currentTime;
      return { ...badges };
    });

    this.syncToLocalStorage();
  }

  public markTabAsUpdated(tab: 'my-cards' | 'my-tasks' | 'my-reviews' | 'my-solicitations'): void {
    const currentTime = Date.now();
    this._badges.update((badges) => {
      badges[tab].lastUpdate = currentTime;
      return { ...badges };
    });

    this.syncToLocalStorage();
  }

  private syncToLocalStorage(): void {
    localStorage.setItem(
      MySpaceBadgesService.MY_SPACE_BADGES_LOCAL_STORAGE_KEY,
      JSON.stringify(this.badges()),
    );
  }
}
